import { useUploadStore } from "@components/FileUpload/uploadStore";
import { VRCertificateTypes, type VRCertificateType } from "@constants/certificates";
import type { VesselRegisterFormData } from "@features/vessel-register/schema/vesselRegisterFormSchema";
import type { Vessel, VesselCertificate } from "@services/vessel/types";
import { normalizeDate, today } from "@utils/date-time";
import type { FieldPath, FieldPathValue, UseFormSetValue } from "react-hook-form";

export const isBunkerBargeInProgress = (vesselDetails?: Vessel): boolean => {
  const vesselStatus = vesselDetails?.vesselRegistrationStatus;
  if (vesselStatus !== "APPROVED" && vesselStatus !== "PENDING_APPROVAL") {
    return false;
  }

  const { effectiveStartDate, effectiveEndDate, gracePeriod } =
    vesselDetails?.bunkerBargeDetails || {};
  const normalizedStartDate = normalizeDate(effectiveStartDate);
  const normalizedEndDate = normalizeDate(effectiveEndDate);
  const normalizedGracePeriod = normalizeDate(gracePeriod);
  const currentDate = today();

  if (!normalizedStartDate || currentDate < normalizedStartDate) {
    return false;
  }

  const effectiveEndDateToUse = normalizedGracePeriod ?? normalizedEndDate;

  return !effectiveEndDateToUse || currentDate <= effectiveEndDateToUse;
};

type CertificateFieldPath = FieldPath<VesselRegisterFormData>;
type SetCertificateStoreValue = <TPath extends CertificateFieldPath>(
  field: TPath,
  value: FieldPathValue<VesselRegisterFormData, TPath>,
) => void;

export const clearCertificates = (
  setValue: UseFormSetValue<VesselRegisterFormData>,
  certsToClear?: VRCertificateType[],
  setStoreValue?: SetCertificateStoreValue,
) => {
  const uploadStore = useUploadStore.getState();

  const certificateTypes = certsToClear ?? Object.values(VRCertificateTypes);
  certificateTypes.forEach((certType) => {
    const fields = getAllCertificateFields(certType);

    setValue(fields.url, "");
    setValue(fields.documentName, "");
    setValue(fields.className, "");
    setValue(fields.expiryDate, null);
    setValue(fields.permanent, false);
    setStoreValue?.(fields.url, "");
    setStoreValue?.(fields.documentName, "");
    setStoreValue?.(fields.className, "");
    setStoreValue?.(fields.expiryDate, null);
    setStoreValue?.(fields.permanent, false);

    uploadStore.clearFilesForKey(certType);
  });
};

const vesselRegistrationCertificateTypes = new Set<string>(Object.values(VRCertificateTypes));

export const getLatestApprovedCertificatesToClear = (
  currentCertificates: VesselRegisterFormData[typeof FORM_FIELDS.CERTIFICATES],
  latestApprovedCertificates: VesselCertificate[],
  eligibleCertificateTypes?: VRCertificateType[],
): VRCertificateType[] =>
  latestApprovedCertificates.reduce<VRCertificateType[]>((acc, certificate) => {
    const certificateType = certificate.certificateType;

    if (!vesselRegistrationCertificateTypes.has(certificateType)) return acc;
    if (
      eligibleCertificateTypes &&
      !eligibleCertificateTypes.includes(certificateType as VRCertificateType)
    ) {
      return acc;
    }

    const currentCertificate = currentCertificates?.[certificateType as VRCertificateType];
    if (
      currentCertificate?.certificateUrl &&
      currentCertificate.certificateUrl === certificate.certificateUrl
    ) {
      acc.push(certificateType as VRCertificateType);
    }

    return acc;
  }, []);

export const CertificateFields = Object.freeze({
  URL: "certificateUrl",
  DOCUMENT_NAME: "certificateDocumentName",
  CLASS_NAME: "certificateClassName",
  EXPIRY_DATE: "certificateExpiryDate",
  PERMANENT: "permanent",
});

export const getAllCertificateFields = (certType: VRCertificateType) => ({
  url: generateCertificateField.url(certType),
  documentName: generateCertificateField.documentName(certType),
  className: generateCertificateField.className(certType),
  expiryDate: generateCertificateField.expiryDate(certType),
  permanent: generateCertificateField.permanent(certType),
});

export const generateCertificateField = {
  root: (certType: VRCertificateType) => `${FORM_FIELDS.CERTIFICATES}.${certType}` as const,
  url: (certType: VRCertificateType) =>
    `${FORM_FIELDS.CERTIFICATES}.${certType}.${CertificateFields.URL}` as const,
  documentName: (certType: VRCertificateType) =>
    `${FORM_FIELDS.CERTIFICATES}.${certType}.${CertificateFields.DOCUMENT_NAME}` as const,
  className: (certType: VRCertificateType) =>
    `${FORM_FIELDS.CERTIFICATES}.${certType}.${CertificateFields.CLASS_NAME}` as const,
  expiryDate: (certType: VRCertificateType) =>
    `${FORM_FIELDS.CERTIFICATES}.${certType}.${CertificateFields.EXPIRY_DATE}` as const,
  permanent: (certType: VRCertificateType) =>
    `${FORM_FIELDS.CERTIFICATES}.${certType}.${CertificateFields.PERMANENT}` as const,
};

export const generateLegacyCertificateField = {
  url: (certType: VRCertificateType) => `${certType}_${CertificateFields.URL}` as const,
  documentName: (certType: VRCertificateType) =>
    `${certType}_${CertificateFields.DOCUMENT_NAME}` as const,
  className: (certType: VRCertificateType) =>
    `${certType}_${CertificateFields.CLASS_NAME}` as const,
  expiryDate: (certType: VRCertificateType) =>
    `${certType}_${CertificateFields.EXPIRY_DATE}` as const,
  permanent: (certType: VRCertificateType) => `${certType}_${CertificateFields.PERMANENT}` as const,
};

export const generateAdditionalDocumentField = {
  root: (idx: number) => `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}` as const,
  url: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.DOCUMENT_URL}` as const,
  documentType: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.DOCUMENT_TYPE}` as const,
  otherSpecifyType: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.OTHER_SPECIFY_TYPE}` as const,
  documentName: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.DOCUMENT_NAME}` as const,
  documentSummary: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.DOCUMENT_SUMMARY}` as const,
  issueDate: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.ISSUE_DATE}` as const,
  expiryDate: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.EXPIRY_DATE}` as const,
  fileName: (idx: number) =>
    `${FORM_FIELDS.ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.FILE_NAME}` as const,
};

export const generateBunkerBargeAdditionalDocumentField = {
  root: (idx: number) => `${FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS}.${idx}` as const,
  documentType: (idx: number) =>
    `${FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.DOCUMENT_TYPE}` as const,
  otherSpecifyType: (idx: number) =>
    `${FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.OTHER_SPECIFY_TYPE}` as const,
  documentName: (idx: number) =>
    `${FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.DOCUMENT_NAME}` as const,
  documentSummary: (idx: number) =>
    `${FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.DOCUMENT_SUMMARY}` as const,
  issueDate: (idx: number) =>
    `${FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.ISSUE_DATE}` as const,
  expiryDate: (idx: number) =>
    `${FORM_FIELDS.BUNKER_BARGE_ADDITIONAL_DOCUMENTS}.${idx}.${FORM_FIELDS.EXPIRY_DATE}` as const,
};

export const FORM_FIELDS = {
  IMO: "imo",
  NO_IMO_NUMBER: "noImoNumber",
  OFFICIAL_NUMBER: "officialNumber",
  VESSEL_NAME: "vesselName",
  CALL_SIGN: "callSign",
  PORT_OF_REGISTRATION: "portOfRegistration",
  YEAR_BUILT: "yearBuilt",
  DATE_OF_DELIVERY: "dateOfDelivery",
  FLAG: "flag",
  VESSEL_TYPE_CODE: "vesselTypeCode",
  BUNKER_REGISTERED: "bunkerRegistered",
  DIPLOMATIC_VESSEL: "diplomaticVessel",
  GROSS_TONNAGE: "grossTonnage",
  NET_TONNAGE: "netTonnage",
  DEADWEIGHT_TONNAGE: "deadweightTonnage",
  LENGTH_OVERALL: "lengthOverall",
  DRAFT_MAX: "draftMax",
  BEAM: "beam",
  VESSEL_CLASSIFICATION_SOCIETY: "vesselClassificationSociety",
  OTHER_VESSEL_CLASSIFICATION_SOCIETY: "otherVesselClassificationSociety",
  SHIP_OWNER: "shipOwner",
  SHIP_CHARTERER: "shipCharterer",
  SHIPS_MANAGEMENT: "shipsManagement",
  SHIPS_REGISTERED_OWNER: "shipsRegisteredOwner",
  PI_CLUB: "piClub",
  MLC_CLUB: "mlcClub",
  PI_CLUB_OTHER: "piClubOther",
  MLC_CLUB_OTHER: "mlcClubOther",
  ADDITIONAL_DOCUMENTS: "additionalDocuments",
  CERTIFICATES: "certificates",
  REGISTRATION_REMARKS: "registrationRemarks",
  DOCUMENT_NAME: "documentName",
  DOCUMENT_URL: "documentUrl",
  FILE_NAME: "fileName",
  DOCUMENT_TYPE: "documentType",
  OTHER_SPECIFY_TYPE: "otherSpecifyType",
  DOCUMENT_SUMMARY: "documentSummary",
  ISSUE_DATE: "issueDate",
  EXPIRY_DATE: "expiryDate",
  NATURE_OF_SERVICE: "natureOfService",
  BUNKERING_COMPANY: "bunkeringCompany",
  NAVIGATION_LICENSE: "navigationLicense",
  NAVIGATION_LICENSE_FROM_DATE: "navigationLicenseFromDate",
  NAVIGATION_LICENSE_TO_DATE: "navigationLicenseToDate",
  NAVIGATION_LICENSE_PERMANENT: "navigationLicensePermanent",
  CHARTER_PARTY_AGREEMENT: "charterPartyAgreement",
  REGISTERED_OWNER: "registeredOwner",
  CHARTER_START_DATE: "charterStartDate",
  CHARTER_END_DATE: "charterEndDate",
  OIL_SPILL_COMPANY: "oilSpillCompany",
  OIL_COMPANY: "oilCompany",
  OIL_SPILL_COMPANY_FROM_DATE: "oilSpillCompanyFromDate",
  OIL_SPILL_COMPANY_TO_DATE: "oilSpillCompanyToDate",
  SIRE_REPORT: "sireReport",
  SIRE_REPORT_FROM_DATE: "sireReportFromDate",
  SIRE_REPORT_TO_DATE: "sireReportToDate",
  HOSE_TEST_CERTIFICATE: "hoseTestCertificate",
  COMPANY_NAME_HOSE_TEST_CERTIFICATE: "companyNameHoseTestCertificate",
  HOSE_TEST_CERTIFICATE_EXPIRY_DATE: "hoseTestCertificateExpiryDate",
  SHIP_TO_SHIP_PLAN: "shipToShipPlan",
  SHIP_TO_SHIP_PLAN_FROM_DATE: "shipToShipPlanFromDate",
  SHIP_TO_SHIP_PLAN_TO_DATE: "shipToShipPlanToDate",
  EFFECTIVE_START_DATE: "effectiveStartDate",
  EFFECTIVE_END_DATE: "effectiveEndDate",
  EFFECTIVE_PERMANENT: "effectivePermanent",
  REMARKS: "remarks",
  BUNKER_BARGE_ADDITIONAL_DOCUMENTS: "bunkerBargeAdditionalDocuments",
} as const;
